import qs from "querystring"

import { useEffect, useState } from "react"
import { useToggle } from "react-use"
import cn from "classnames"

import { getErrorDescription } from "modules/discount/getErrorDescription"
import {
    ClientDiscount,
    DiscountByPromoCodeResponse,
    ErrorResponse,
    OfferedService,
    Psychologist,
    RequestStatus,
} from "utils/interfaces"
import { sortDiscounts } from "utils/helpers"
import { getDiscounts } from "modules/discount/api"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { BannerStorage } from "modules/discount/BannerStorage"
import { DiscountList } from "modules/discount/DiscountList"
import { VerificationCodeModal } from "modules/discount/VerificationCodeModal"
import { PromoCode } from "modules/discount/PromoCode"
import { Img, Text } from "ui-kit/components-branded/index"
import { useDidMount } from "utils/customHooks"
import { httpClient, toAxiosError } from "utils/infrastructure/httpClient"

interface Props {
    hasPreviousSessions?: boolean
    className?: string
    email: string
    psy: Psychologist
    selectedService: OfferedService | undefined
    selectedDiscount: number | undefined
    bookingId?: number
    date?: string
    onBeforeAddDiscount?: () => boolean
    onChangeDiscount: (discountId?: number) => void
    onCanChangeHistory?: (value?: boolean) => void
}

export function Discounts({
    hasPreviousSessions,
    className,
    email,
    psy,
    selectedService,
    selectedDiscount,
    bookingId,
    date,
    onBeforeAddDiscount,
    onChangeDiscount,
    onCanChangeHistory,
}: Props) {
    const [, { reloadClient }] = useClientContext()
    const [discounts, setDiscounts] = useState<ClientDiscount[] | undefined>(undefined)
    const [discountsStatus, setDiscountsStatus] = useState<RequestStatus>("idle")
    const [showVerification, setShowVerification] = useState(false)
    const [promoCode, setPromoCode] = useState(BannerStorage.getPromoCode() || "")
    const [promoCodeDisabled, setPromoCodeDisabled] = useState(false)
    const [promoCodeError, setPromoCodeError] = useState<string | undefined>()
    const [promoCodeSuccess, setPromoCodeSuccess] = useState<string | undefined>(undefined)
    const [show, setShow] = useState(false)
    const [showPromoCode, toggleShowPromoCode] = useToggle(true)
    const [canPromoCodeSelect, toggleCanPromoCodeSelect] = useToggle(true)

    const resetMessages = () => {
        setPromoCodeError(undefined)
        setPromoCodeSuccess(undefined)
    }

    const updateDiscounts = () => {
        setDiscountsStatus("loading")
        getDiscounts(selectedService?.id, bookingId, date)
            .then((data) => {
                setDiscounts(data)
                setDiscountsStatus("ready")
            })
            .catch(() => {
                setDiscountsStatus("error")
            })
    }

    const changePromoCode = (promoCode: string) => {
        resetMessages()
        setPromoCode(promoCode)
    }

    const verificationCompleted = async () => {
        toggleCanPromoCodeSelect(false)
        setShowVerification(false)
        onCanChangeHistory?.(false)
        await reloadClient?.()
        await addDiscount()
    }

    const verificationCanceled = () => {
        setShowVerification(false)
        resetMessages()
        setPromoCode("")
    }

    const addDiscount = async () => {
        resetMessages()
        const response = await getDiscount(promoCode)
        if (response.error === "login_required") {
            setShowVerification(true)
            return
        }

        if (response.error) {
            setPromoCodeError(getErrorDescription(response.error))
            setShow(true)
            return
        }

        if (response.discountId === undefined) {
            setPromoCodeError("Произошла ошибка применения промокода")
            setShow(true)
            return
        }

        onChangeDiscount(response.discountId)
        setPromoCodeSuccess("Промокод успешно применен")
        setPromoCodeDisabled(true)
        setShow(true)
    }

    const setDiscount = (id: number | undefined) => {
        resetMessages()
        onChangeDiscount(id)
        setPromoCodeDisabled(false)

        toggleShowPromoCode(!id)
    }

    const applyPromoCode = async () => {
        if (onBeforeAddDiscount && !onBeforeAddDiscount()) return
        await addDiscount()
    }

    const getDiscount = async (promoCode: string) => {
        if (!selectedService) throw new Error("selectedService for discount is undefined")

        const params = bookingId
            ? { booking_id: bookingId, promo_code: promoCode }
            : {
                  promo_code: promoCode,
                  psy_hash_id: psy.hash_id,
                  offered_service_id: selectedService.id,
                  email: email,
              }
        try {
            const data = await httpClient.get<DiscountByPromoCodeResponse>(
                `pricing/discount/by-promo-code?${qs.stringify(params)}`
            )
            return { discountId: data.id, error: undefined }
        } catch (e) {
            return {
                discountId: undefined,
                error: toAxiosError<ErrorResponse>(e)?.response?.data.error,
            }
        }
    }

    useDidMount(() => {
        void updateDiscounts()
    })

    useEffect(() => {
        if (discountsStatus === "ready" && discounts && discounts.length > 0) {
            resetMessages()
            const firstDiscounts = sortDiscounts(discounts)[0]
            const discountId = firstDiscounts.can_be_applied
                ? firstDiscounts.discount_id
                : undefined
            onChangeDiscount(discountId)
            toggleShowPromoCode(!discountId)
        }
    }, [discountsStatus, discounts])

    const handlePromoCodeSelect = () => {
        if (!canPromoCodeSelect) {
            toggleCanPromoCodeSelect(true)
            return
        }
        setDiscount(undefined)
    }

    return (
        <div className={cn(className)}>
            {!show && hasPreviousSessions && discounts && discounts.length === 0 && (
                <Text
                    onClick={() => setShow(!show)}
                    className="booking-form-discount__title cursor-pointer"
                >
                    Есть промокод
                </Text>
            )}
            {(show || !hasPreviousSessions || (discounts && discounts?.length > 0)) && (
                <>
                    {discounts?.length ? (
                        <>
                            <div className="is-flex is-align-items-center">
                                <Img src="discounts.png" width={18} height={16} />
                                <Text
                                    className="px-1 is-flex-grow-1"
                                    isAccent={!!discounts?.length}
                                >
                                    Скидка
                                </Text>
                            </div>
                            <DiscountList
                                selectedDiscount={selectedDiscount}
                                showSelect
                                discounts={discounts}
                                onDiscountChange={setDiscount}
                                hasPreviousSessions={hasPreviousSessions}
                            />
                        </>
                    ) : null}
                    {showPromoCode && (
                        <PromoCode
                            isShowLabel={!discounts?.length}
                            promoCode={promoCode}
                            promoCodeSuccess={promoCodeSuccess}
                            promoCodeError={promoCodeError}
                            promoCodeDisabled={promoCodeDisabled}
                            onPromoCodeChange={changePromoCode}
                            onApplyPromoCode={applyPromoCode}
                            onPromoCodeSelect={handlePromoCodeSelect}
                            hasPreviousSessions={hasPreviousSessions}
                            focusOnPromoCode={!!discounts?.length}
                        />
                    )}
                    <VerificationCodeModal
                        enable={!!email && showVerification}
                        onVerified={verificationCompleted}
                        onCancel={verificationCanceled}
                        email={email}
                    />
                </>
            )}
        </div>
    )
}
